import { AuthorityBadge } from '../components/AuthorityBadge';
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { InteractiveInsightCard } from '../components/interactive/InteractiveInsightCard'

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
}

const METRIC_CARDS = [
  {
    value: '12+',
    label: 'Sacred Canons',
    sublabel: 'Foundational texts analyzed',
    color: 'amber',
    details: {
      explanation: 'Godly Library examines the foundational scriptures of world traditions: Torah, Bible, Quran, Vedas, Tripitaka, Guru Granth Sahib, Tao Te Ching, Analects, Avesta, Agamas, and more. Each canon is analyzed with philological rigor and historical context.',
      relatedConcepts: ['Canon Formation', 'Textual Criticism', 'Sacred Literature'],
      references: [{ title: 'Wilfred Cantwell Smith - What Is Scripture? (1993)' }],
      quiz: {
        question: 'Which scripture is considered the oldest among major world religions?',
        options: ['Torah', 'Rigveda', 'Avesta', 'Tripitaka'],
        correctIndex: 1,
        explanation: 'The Rigveda, composed roughly 1500-1200 BCE, is among the oldest known religious texts still in active liturgical use.'
      }
    }
  },
  {
    value: '5000+',
    label: 'Years of Wisdom',
    sublabel: 'From ancient texts to modern theology',
    color: 'indigo',
    details: {
      explanation: 'Human spiritual literature spans over five millennia, from the earliest Sumerian hymns and Egyptian pyramid texts through Vedic composition, biblical authorship, Quranic revelation, and modern theological discourse.',
      relatedConcepts: ['Ancient Near East', 'Oral Tradition', 'Written Canon'],
      references: [{ title: 'Karen Armstrong - The Great Transformation (2006)' }],
      quiz: {
        question: 'The "Axial Age" (800-200 BCE) saw the emergence of which traditions?',
        options: ['Only Buddhism', 'Greek philosophy, Buddhism, Confucianism, Hebrew prophecy', 'Only Christianity and Islam', 'Only Hinduism'],
        correctIndex: 1,
        explanation: 'Karl Jaspers coined "Axial Age" for the period when Greek philosophy, Buddhism, Confucianism, Zoroastrianism, and Hebrew prophecy all emerged.'
      }
    }
  },
  {
    value: 'Cross-',
    label: 'Textual Analysis',
    sublabel: 'Comparing sacred narratives',
    color: 'emerald',
    details: {
      explanation: 'Cross-textual analysis compares themes, literary forms, and theological claims across scriptures without privileging any tradition. Flood narratives, creation accounts, wisdom literature, and eschatological visions appear across many canons.',
      relatedConcepts: ['Comparative Literature', 'Intertextuality', 'Narrative Theology'],
      references: [{ title: 'Raimon Panikkar - The Intrareligious Dialogue (1999)' }],
      quiz: {
        question: 'Flood narratives appear in which traditions?',
        options: ['Only the Bible', 'Bible and Quran only', 'Mesopotamian, Biblical, Hindu, Greek, and many Indigenous traditions', 'Only Abrahamic faiths'],
        correctIndex: 2,
        explanation: 'Flood narratives are nearly universal: Gilgamesh, Genesis, Quran, Hindu Matsya Purana, Greek Deucalion, and many Indigenous traditions.'
      }
    }
  },
  {
    value: 'Equal',
    label: 'Reverence',
    sublabel: 'No tradition privileged',
    color: 'rose',
    details: {
      explanation: 'Godly Library treats every sacred text with equal scholarly reverence. Vedic hymns receive the same analytical depth as Biblical psalms. Sufi poetry is examined with the same rigor as Thomistic philosophy. The goal is understanding, not ranking.',
      relatedConcepts: ['Epoche', 'Comparative Theology', 'Hermeneutical Circle'],
      references: [{ title: 'Francis X. Clooney - Comparative Theology (2010)' }],
      quiz: {
        question: 'What is "comparative theology" as a discipline?',
        options: ['Proving one religion superior', 'Deep study of another tradition to enrich understanding', 'Merging all religions', 'Studying only similarities'],
        correctIndex: 1,
        explanation: 'Comparative theology involves deep, respectful engagement with another tradition to gain new insights and enrich one\'s own understanding.'
      }
    }
  }
]

const TOPIC_CARDS = [
  {
    title: 'Sacred Text Hermeneutics',
    color: 'amber',
    description: 'How traditions interpret their foundational texts: PaRDeS in Judaism, Tafsir in Islam, lectio divina in Christianity, bhashya in Hinduism.',
    details: {
      explanation: 'Every tradition develops sophisticated methods for interpreting sacred texts. Jewish PaRDeS offers four levels (literal, allusive, homiletical, mystical). Islamic Tafsir employs linguistic, historical, and thematic analysis. Christian exegesis ranges from patristic allegory to modern historical-critical method. Hindu commentary traditions (bhashya) build elaborate philosophical systems on textual foundations.',
      example: 'PaRDeS: Peshat (plain meaning), Remez (hint), Derash (homily), Sod (secret/mystical)',
      relatedConcepts: ['PaRDeS', 'Tafsir', 'Lectio Divina', 'Bhashya'],
      references: [{ title: 'Sandra Schneiders - The Revelatory Text (1999)' }],
      quiz: {
        question: 'What does "PaRDeS" refer to in Jewish hermeneutics?',
        options: ['A garden in Eden', 'Four levels of textual interpretation', 'A type of prayer', 'A legal category'],
        correctIndex: 1,
        explanation: 'PaRDeS is an acronym for four levels: Peshat (literal), Remez (allegorical), Derash (homiletical), Sod (mystical).'
      }
    }
  },
  {
    title: 'Theology of the Word',
    color: 'indigo',
    description: 'How traditions understand divine communication: Torah from Sinai, Quran as direct speech of God, Veda as shruti (heard), logos in Christianity.',
    details: {
      explanation: 'Traditions differ profoundly in how they understand divine communication. Judaism sees Torah as given at Sinai through Moses. Islam holds the Quran to be the literal, uncreated word of God in Arabic. Hinduism considers the Vedas to be shruti (heard by sages), eternal and authorless. Christianity identifies Christ as the incarnate Word (logos).',
      example: 'Islamic view: The Quran is not "inspired" but is God\'s own speech, dictated to Muhammad through Gabriel.',
      relatedConcepts: ['Revelation', 'Shruti', 'Logos', 'Wahy'],
      references: [{ title: 'William Graham - Beyond the Written Word (1987)' }],
      quiz: {
        question: 'What does "shruti" mean in Hindu scripture classification?',
        options: ['Written by sages', 'That which is heard (divinely revealed)', 'Commentary tradition', 'Folk wisdom'],
        correctIndex: 1,
        explanation: 'Shruti ("that which is heard") refers to the Vedas as eternal truths perceived by ancient sages, distinguished from smriti ("remembered").'
      }
    }
  },
  {
    title: 'Ethical Commands Across Traditions',
    color: 'emerald',
    description: 'Moral imperatives in scripture: 613 mitzvot, Five Pillars, Ten Commandments, Eightfold Path, Confucian virtues.',
    details: {
      explanation: 'Sacred texts encode ethical frameworks that shape civilizations. Judaism\'s 613 commandments (mitzvot) govern all aspects of life. Islam\'s Five Pillars structure devotional practice. The Buddhist Eightfold Path guides ethical conduct and mental cultivation. Confucian ren (benevolence) and li (ritual propriety) shaped East Asian civilization.',
      example: 'The Golden Rule appears independently: Hillel\'s formulation, Jesus\'s Sermon on the Mount, Confucius\'s Analects 15:23.',
      relatedConcepts: ['Mitzvot', 'Five Pillars', 'Eightfold Path', 'Ren and Li'],
      references: [{ title: 'Hans Kung - Global Ethic Project (1993)' }],
      quiz: {
        question: 'How many commandments (mitzvot) does Jewish tradition identify in the Torah?',
        options: ['10', '248', '613', '1000'],
        correctIndex: 2,
        explanation: 'Traditional enumeration identifies 613 mitzvot in the Torah: 248 positive commands and 365 prohibitions.'
      }
    }
  },
  {
    title: 'Eschatology Across Canons',
    color: 'rose',
    description: 'End-times visions: Messianic age, Day of Judgment, Buddhist ages of dharma decline, Hindu yugas, Ragnarok.',
    details: {
      explanation: 'Sacred texts envision the culmination of history in diverse ways. Judaism awaits the Messianic age. Christianity anticipates the Second Coming. Islam describes the Day of Judgment (Yawm al-Qiyamah). Buddhism teaches cycles of dharma decline and renewal. Hinduism describes the four yugas culminating in cosmic dissolution (pralaya).',
      example: 'Hindu four yugas: Satya (truth), Treta, Dvapara, Kali (current age of decline), followed by renewal.',
      relatedConcepts: ['Messianism', 'Apocalypse', 'Yuga Cycle', 'Maitreya'],
      references: [{ title: 'John Collins - The Apocalyptic Imagination (2016)' }],
      quiz: {
        question: 'In Buddhist eschatology, who is the future Buddha expected to come?',
        options: ['Krishna', 'Maitreya', 'Mahdi', 'Kalki'],
        correctIndex: 1,
        explanation: 'Maitreya is the prophesied future Buddha who will appear when the dharma has been completely forgotten.'
      }
    }
  }
]

const HERITAGE_CARDS = [
  {
    title: 'Oral to Written',
    color: 'amber',
    description: 'The transition from oral to written scripture: Torah\'s literary formation, Quran\'s compilation under Uthman, Buddhist councils, Vedic memorization.',
    details: {
      explanation: 'Most sacred texts began as oral traditions before being committed to writing. The Torah underwent centuries of literary development. The Quran was compiled into a standard codex under Caliph Uthman. Buddhist councils formalized the Pali Canon centuries after the Buddha. The Vedas were transmitted through elaborate memorization techniques for millennia.',
      relatedConcepts: ['Documentary Hypothesis', 'Uthmanic Codex', 'Buddhist Councils'],
      references: [{ title: 'Bart Ehrman - Misquoting Jesus (2005)' }],
    }
  },
  {
    title: 'Translation and Transmission',
    color: 'emerald',
    description: 'How scriptures cross linguistic and cultural boundaries: Septuagint, Vulgate, Tibetan Buddhist canon, Chinese Tripitaka.',
    details: {
      explanation: 'Sacred texts travel across languages and cultures, gaining new meanings. The Septuagint translated Hebrew Bible into Greek. Jerome\'s Vulgate rendered it into Latin. Kumarajiva and Xuanzang translated Buddhist sutras into Chinese. Each translation is also an interpretation, shaping how communities understand their scripture.',
      relatedConcepts: ['Septuagint', 'Vulgate', 'Kumarajiva', 'Luther\'s Bible'],
      references: [{ title: 'Lamin Sanneh - Translating the Message (2009)' }],
    }
  },
  {
    title: 'Living Scripture',
    color: 'cyan',
    description: 'How communities keep texts alive through liturgy, chanting, calligraphy, and ritual reading across all traditions.',
    details: {
      explanation: 'Sacred texts are not merely studied but performed. Jewish Torah reading follows an annual cycle. Quran recitation (tilawah) is itself an act of worship. Buddhist chanting embodies the dharma. Hindu mantra recitation activates spiritual power. These performative dimensions make scripture a living presence in community life.',
      relatedConcepts: ['Liturgy', 'Tilawah', 'Lectionary', 'Mantra'],
      references: [{ title: 'William Graham - Beyond the Written Word (1987)' }],
    }
  }
]

export default function Home() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16 lg:py-20">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-[-20%] left-[20%] w-[500px] h-[500px] rounded-full bg-amber-500/5 blur-3xl" />
          <div className="absolute top-[10%] right-[10%] w-[400px] h-[400px] rounded-full bg-indigo-500/5 blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          <div className="inline-block mb-6 px-3 py-1 rounded-full border border-amber-400/30 bg-amber-500/5">
            <span className="text-xs font-medium text-amber-400 dark:text-amber-400">
              Comparative Sacred Texts Archive
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight text-slate-900 dark:text-white">
            <span className="bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent">Godly Library</span> -
            <br className="hidden sm:block" /> Sacred Texts Analysis Across Traditions
          </h1>

      {/* E-E-A-T Authority Signals */}
      <AuthorityBadge
        lastUpdated="February 2026"
        researchInitiative="Godly Library Theology Research"
        sourceCount={8}
        domain="godlylibrary.com"
      />

          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 mb-4 max-w-2xl mx-auto leading-relaxed">
            A scholarly archive examining humanity's sacred canons with philological rigor and equal reverence.
            From Vedic hymns to Quranic surahs, from Biblical psalms to Buddhist sutras, every text analyzed
            on its own terms.
          </p>

          <p className="text-sm text-slate-500 mb-8 sm:mb-10">
            Cross-textual analysis of Torah, Bible, Quran, Vedas, Tripitaka, Guru Granth Sahib, Tao Te Ching, and more.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/demo"
              className="px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-base sm:text-lg transition-colors duration-300 min-h-[44px] text-center"
            >
              Explore Sacred Texts
            </Link>
          </div>
        </motion.div>
      </div>

      <div className="w-24 h-px mx-auto mt-10 mb-6 bg-gradient-to-r from-transparent via-amber-400/40 to-transparent" />

      <motion.div
        className="flex flex-wrap gap-3 items-center text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <span>Last Updated: February 2026</span>
        <span className="hidden sm:inline text-slate-300 dark:text-slate-600">|</span>
        <span>Godly Library Comparative Research</span>
        <span className="hidden sm:inline text-slate-300 dark:text-slate-600">|</span>
        <span>10 Scholarly Sources</span>
      </motion.div>

      {/* Metric Cards */}
      <motion.div id="features"
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.2 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-12 sm:mt-16"
      >
        {METRIC_CARDS.map((item) => (
          <InteractiveInsightCard
            key={item.label}
            title={item.label}
            value={item.value}
            label={item.label}
            sublabel={item.sublabel}
            color={item.color}
            details={item.details}
            className="text-center"
          />
        ))}
      </motion.div>

      {/* Definition */}
      <motion.section id="definition"
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-16 sm:mt-20"
      >
        <div className="p-6 rounded-xl border border-amber-400/20 bg-slate-900/50">
          <h2 className="text-xl font-bold text-white mb-4">
            The Study of Sacred Texts
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Sacred texts are the foundational documents of human civilization. They encode cosmologies,
            ethical frameworks, legal systems, mystical insights, and literary achievements that have
            shaped billions of lives across millennia.
          </p>
          <p className="text-sm text-slate-400">
            Godly Library approaches these texts as a comparative archive: examining how different traditions
            understand revelation, interpret their canons, and transmit sacred knowledge across generations.
            Every text receives equal scholarly attention and reverence.
          </p>
        </div>
      </motion.section>

      {/* Topic Cards */}
      <motion.section
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.35 }}
        className="mt-16 sm:mt-20"
      >
        <div className="p-6 rounded-xl border border-indigo-400/20 bg-slate-900/50">
          <h2 className="text-xl font-bold text-white mb-4">
            Core Areas of Sacred Text Study
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            Exploring how humanity's traditions create, interpret, and live by their foundational texts.
            <span className="text-indigo-400 ml-1">Click any topic to learn more and test your knowledge.</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {TOPIC_CARDS.map((topic) => (
              <InteractiveInsightCard
                key={topic.title}
                title={topic.title}
                description={topic.description}
                color={topic.color}
                details={topic.details}
              />
            ))}
          </div>
        </div>
      </motion.section>

      {/* Heritage Cards */}
      <motion.section
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-16 sm:mt-20"
      >
        <div className="p-6 rounded-xl border border-amber-400/20 bg-slate-900/50">
          <h2 className="text-xl font-bold text-white mb-4">
            Scripture Through the Ages
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            How sacred texts travel through time, language, and culture.
            <span className="text-amber-400 ml-1">Click to explore each dimension.</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {HERITAGE_CARDS.map((heritage) => (
              <InteractiveInsightCard
                key={heritage.title}
                title={heritage.title}
                description={heritage.description}
                color={heritage.color}
                details={heritage.details}
              />
            ))}
          </div>
        </div>
      </motion.section>

      {/* Applications */}
      <motion.section id="applications"
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.45 }}
        className="mt-16 sm:mt-20"
      >
        <div className="p-6 rounded-xl border border-purple-400/20 bg-slate-900/50">
          <h2 className="text-xl font-bold text-white mb-4">
            Why Compare Sacred Texts?
          </h2>
          <div className="space-y-4 text-sm text-slate-400">
            <p>
              <strong className="text-purple-400">Deeper Understanding:</strong> Examining how different
              traditions approach the same themes (creation, ethics, eschatology) illuminates each tradition's
              distinctive genius while revealing shared human concerns.
            </p>
            <p>
              <strong className="text-purple-400">Hermeneutical Insight:</strong> Comparing interpretive
              methods (PaRDeS, Tafsir, lectio divina) enriches our understanding of how communities
              derive meaning from foundational texts.
            </p>
            <p>
              <strong className="text-purple-400">Living Traditions:</strong> Sacred texts are not museum
              artifacts but living documents that continue to shape law, ethics, art, and community life
              for billions of people today.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Demo CTA */}
      <motion.div
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.5 }}
        className="mt-16 sm:mt-20 text-center"
      >
        <div className="p-6 rounded-xl border border-amber-400/30 bg-gradient-to-br from-amber-950/20 to-slate-900">
          <h3 className="text-xl font-bold text-amber-400 mb-3">Begin Your Sacred Journey</h3>
          <p className="text-sm text-slate-300 mb-4">
            Explore the interactive demonstration of sacred text analysis across all traditions.
          </p>
          <Link
            to="/demo"
            className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm sm:text-base transition-colors duration-300 inline-block min-h-[44px]"
          >
            Enter the Sacred Library
          </Link>
        </div>
      </motion.div>

      {/* References */}
      <motion.section
        variants={fadeUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 0.55 }}
        className="mt-16 sm:mt-20"
      >
        <div className="p-6 rounded-xl border border-slate-600/20 bg-slate-900/30">
          <h3 className="text-lg font-bold text-slate-300 mb-4">Selected References</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 text-xs text-slate-500">
            <div>
              <p className="text-slate-400 font-medium">Scripture Studies</p>
              <p>W.C. Smith. <em>What Is Scripture?</em> (1993)</p>
              <p>Graham. <em>Beyond the Written Word</em> (1987)</p>
            </div>
            <div>
              <p className="text-slate-400 font-medium">Comparative Theology</p>
              <p>Clooney. <em>Comparative Theology</em> (2010)</p>
              <p>Panikkar. <em>The Intrareligious Dialogue</em> (1999)</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Related Topics */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="mt-16 sm:mt-20"
      >
        <div className="p-6 rounded-xl border border-slate-600/20 bg-slate-900/30">
          <h3 className="text-lg font-bold text-slate-300 mb-2">Related Topics in the Knowledge Network</h3>
          <p className="text-sm text-slate-400 mb-4">Explore related subjects across the Global Knowledge Graph Network.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <a href="https://godlibrary.com" target="_blank" rel="noopener noreferrer" className="block p-4 rounded-xl border border-slate-700 hover:border-amber-500/50 transition-all duration-300">
              <h4 className="font-semibold text-white mb-1">GodLibrary</h4>
              <p className="text-sm text-slate-400">Interfaith philosophical arguments</p>
            </a>
            <a href="https://truthgraph.com" target="_blank" rel="noopener noreferrer" className="block p-4 rounded-xl border border-slate-700 hover:border-amber-500/50 transition-all duration-300">
              <h4 className="font-semibold text-white mb-1">TruthGraph</h4>
              <p className="text-sm text-slate-400">Formal logic and argumentation</p>
            </a>
            <a href="https://alephinity.com" target="_blank" rel="noopener noreferrer" className="block p-4 rounded-xl border border-slate-700 hover:border-amber-500/50 transition-all duration-300">
              <h4 className="font-semibold text-white mb-1">Alephinity</h4>
              <p className="text-sm text-slate-400">Transfinite mathematics</p>
            </a>
          </div>
          <p className="text-xs text-slate-500 mt-4 text-center">
            <a href="https://globalknowledgegraph.com" className="text-blue-400 hover:underline transition-colors duration-300">
              Part of the Global Knowledge Graph Network
            </a>
          </p>
        </div>
      </motion.section>
    </div>
  )
}
